import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import QueryString from "qs";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { toast } from "react-toastify";
import { useAuth } from "../../../../Context/AuthContext";
import { modelTestQuestionUrls, orderUrls } from "../../../../Api/Auth/ApiUrls";
import { sendPutRequestAuth } from "../../../../Api/swrConfig";
import Tabs from "../../../../Components/Common/Tabs";
import { studentSimulatorTabsData } from "../../../../data/SidebarData";
import Breadcrumb from "../../../../Components/Common/Breadcrumb";
import { Loader } from "../../../../Components/Common/Loader";
import { publicUrls, studentUrls } from "../../../../Api/Auth/AppUrls";
import Button from "../../../../Components/Common/Button";
import SimulatorModal from "../Components/SimulatorModal";
import { checkStudentRouteVisibility } from "../../../../utils/findStudentRouteBySlug";
import NotFound from "../../../Notfound";

const MechanicalStudentSimulator = () => {
  const { userData, categoryData } = useAuth();
  const location = useLocation();
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("exam"); // "exam" or "review"
  const [selectedAttemptNumber, setSelectedAttemptNumber] = useState(null);
  const parts = location.pathname.split("/").filter(Boolean);
  const dynamicPath = `/${parts[0]}/${parts[1]}-${parts[2]}`;
  const dynamicSlug = dynamicPath.split("/")[dynamicPath.split("/").length - 1];
  const isVisibleCivil = checkStudentRouteVisibility(
    categoryData,
    studentUrls.studentSimulatorCivil
  );
  const isVisibleMechanical = checkStudentRouteVisibility(
    categoryData,
    studentUrls.studentSimulatorMechanical
  );
  const visibleTabs = studentSimulatorTabsData.filter((tab) => {
    if (!tab?.path) return true;
    if (tab.path === studentUrls.studentSimulatorCivil && !isVisibleCivil)
      return false;
    if (
      tab.path === studentUrls.studentSimulatorMechanical &&
      !isVisibleMechanical
    )
      return false;
    return true;
  });

  const statusId = selected?.statusInfo?._id;
  const userId = userData?._id;

  const apiQueryParams = {
    userId: userData?._id,
    itemType: "subscription",
    slug: dynamicSlug,
  };

  const apiQueryString = QueryString.stringify(apiQueryParams);
  const { isLoading, data, isValidating, mutate } = useSWR(
    orderUrls.purchases(apiQueryString)
  );

  // SWR Mutation for saving progress
  const { trigger: saveProgress } = useSWRMutation(
    modelTestQuestionUrls.exam(statusId),
    sendPutRequestAuth,
    {
      onSuccess: (data, key, config) => {
        console.log(config);

        // FIXED: Only auto-open modal for new exam starts, not for resume operations
        if (config?.openModalOnSuccess !== false) {
          setShowModal(true);
        }
        // Mutate the data to refresh the list and update attempt counts
        mutate();
      },
    }
  );

  // Helper function to check remaining attempts
  const getRemainingAttempts = (test) => {
    const maxAttempts = test.maxAttempts || 0;
    const completedAttempts = test.statusInfo?.attempts || 0;

    // Check if current attempt is in progress
    const isInProgress =
      test.statusInfo?.status === "in-progress" ||
      test.statusInfo?.status === "paused";

    // If in progress, the current attempt is not yet completed, so don't subtract it
    if (isInProgress) {
      return maxAttempts - completedAttempts;
    }

    // No attempt in progress, so remaining = max - completed
    return maxAttempts - completedAttempts;
  };

  // Check if user can interact with exam (start new OR continue existing)
  const canInteractWithExam = (test) => {
    const remainingAttempts = getRemainingAttempts(test);
    const isInProgress =
      test.statusInfo?.status === "in-progress" ||
      test.statusInfo?.status === "paused";

    // Can interact if: has remaining attempts OR has an exam in progress
    return remainingAttempts > 0 || isInProgress;
  };

  // FIXED: Handler for starting a new exam attempt or continuing existing one
  // FIXED: Handler for starting a new exam attempt or continuing existing one
  const onStartClick = (test) => {
    const remainingAttempts = getRemainingAttempts(test);
    const status = test.statusInfo?.status || "pending";
    const isInProgress = status === "in-progress" || status === "paused";
    const isPending = status === "pending";
    const isCompleted = status === "completed";

    // Check if no attempts remaining (except for in-progress exams)
    if (remainingAttempts <= 0 && !isInProgress) {
      toast.error(
        "No attempts remaining. You have reached the maximum number of attempts for this exam."
      );
      return;
    }

    if (isPending) {
      // Starting a new exam - initialize proper attempt data
      const currentTime = new Date();
      const newAttemptNumber = 1; // First attempt is always 1

      // Set up the exam interface first
      setSelected(test);
      setModalMode("exam");
      setSelectedAttemptNumber(newAttemptNumber);

      saveProgress(
        {
          user: userId,
          modelTest: test._id,
          status: "in-progress",
          currentAttempt: newAttemptNumber,
          attempts: 0, // Number of completed attempts (still 0 since we're starting)
          currentQuestionIndex: 0, // Start from first question
          answers: [], // Empty answers array for new attempt
          score: 0, // Reset score for new attempt
          timeTaken: 0, // Reset time for new attempt
          startedAt: currentTime, // Set current timestamp
          pausedAt: null,
          completedAt: null,
          remainingTime: test.duration ? test.duration * 3600 : null, // Convert hours to seconds if timed
        },
        { openModalOnSuccess: true } // Open modal after successful save
      );
      return;
    }

    // Handle resuming in-progress or paused exam
    if (isInProgress) {
      // Resume/Continue existing attempt
      setSelected(test);
      setModalMode("exam");
      setSelectedAttemptNumber(test.statusInfo?.currentAttempt || 1);

      // Show modal immediately
      setShowModal(true);

      // Update status to in-progress if it was paused (do this in background)
      if (status === "paused") {
        saveProgress(
          {
            user: userId,
            modelTest: test._id,
            status: "in-progress",
            currentAttempt: test.statusInfo.currentAttempt,
            attempts: test.statusInfo.attempts, // Keep existing completed attempts count
            currentQuestionIndex: test.statusInfo.currentQuestionIndex || 0,
            answers: test.statusInfo.answers || [], // Keep existing answers
            score: test.statusInfo.score || 0,
            timeTaken: test.statusInfo.timeTaken || 0,
            startedAt: test.statusInfo.startedAt,
            pausedAt: null, // Clear pause timestamp
            completedAt: null,
            remainingTime: test.statusInfo.remainingTime,
          },
          { openModalOnSuccess: false }
        );
      }
      return;
    }

    // Handle completed status - start new attempt
    if (isCompleted && remainingAttempts > 0) {
      const currentTime = new Date();
      const newAttemptNumber = (test.statusInfo?.currentAttempt || 0) + 1;

      // Set up the exam interface first
      setSelected(test);
      setModalMode("exam");
      setSelectedAttemptNumber(newAttemptNumber);

      saveProgress(
        {
          user: userId,
          modelTest: test._id,
          status: "in-progress",
          currentAttempt: newAttemptNumber,
          attempts: test.statusInfo?.attempts || 0, // Keep existing completed attempts count
          currentQuestionIndex: 0, // Start from first question
          answers: test.statusInfo?.answers || [], // Keep all previous answers
          score: 0, // Reset score for new attempt (this is current attempt score)
          timeTaken: 0, // Reset time for new attempt (this is current attempt time)
          startedAt: currentTime, // Set current timestamp
          pausedAt: null,
          completedAt: null,
          remainingTime: test.duration ? test.duration * 3600 : null, // Convert hours to seconds if timed
          // Keep existing attemptScores
          attemptScores: test.statusInfo?.attemptScores || [],
        },
        { openModalOnSuccess: true } // Open modal after successful save
      );
      return;
    }
  };
  // Handler for viewing previous attempts
  const onSeeAttemptsClick = (test) => {
    console.log("Checking attempts for test:", test);

    // Check if we have completed attempts by looking at attemptScores in statusInfo
    const hasAttemptScores =
      test.statusInfo?.attemptScores &&
      test.statusInfo.attemptScores.length > 0;

    const hasCompletedAttempts =
      test.statusInfo?.attempts > 0 || hasAttemptScores;

    console.log("Has completed attempts:", hasCompletedAttempts);
    console.log("Attempt scores:", test.statusInfo?.attemptScores);

    if (!hasCompletedAttempts) {
      toast.info("No completed attempts found for this exam.");
      return;
    }

    setSelected(test);
    setModalMode("review");

    // Set to the latest completed attempt or the first available
    let defaultAttempt = 1;
    if (hasAttemptScores) {
      const attemptNumbers = test.statusInfo.attemptScores.map(
        (a) => a.attemptNumber
      );
      defaultAttempt = Math.max(...attemptNumbers);
    } else if (test.statusInfo?.attempts > 0) {
      defaultAttempt = test.statusInfo.attempts;
    }

    console.log("Setting default attempt to:", defaultAttempt);
    setSelectedAttemptNumber(defaultAttempt);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelected(null);
    setModalMode("exam");
    setSelectedAttemptNumber(null);
    // Refresh the data to update attempt counts
    mutate();
  };

  // Get display text for attempt button
  const getAttemptButtonText = (item) => {
    const isInProgress =
      item.statusInfo?.status === "in-progress" ||
      item.statusInfo?.status === "paused";

    if (isInProgress) {
      return item.statusInfo.status === "paused"
        ? "Resume Exam"
        : "Continue Exam";
    }

    const remainingAttempts = getRemainingAttempts(item);
    if (remainingAttempts <= 0) {
      return "No Attempts Left";
    }

    return "Start Exam";
  };

  // Get attempt status display
  const getAttemptStatusDisplay = (item) => {
    const completedAttempts = item.statusInfo?.attempts || 0;
    const maxAttempts = item.maxAttempts || 0;
    const isInProgress =
      item.statusInfo?.status === "in-progress" ||
      item.statusInfo?.status === "paused";

    if (isInProgress) {
      return (
        <div className="text-xs text-orange-600 text-center font-medium">
          Attempt #{item.statusInfo.currentAttempt} in progress
          {item.statusInfo.status === "paused" && " (Paused)"}
        </div>
      );
    }

    if (completedAttempts > 0) {
      const remainingAttempts = getRemainingAttempts(item);
      return (
        <div className="text-xs text-gray-600 text-center">
          <div>
            Completed: {completedAttempts}/{maxAttempts} attempts
          </div>
          {remainingAttempts > 0 ? (
            <div className="text-blue-600 font-medium">
              {remainingAttempts} attempts remaining
            </div>
          ) : (
            <div className="text-red-600 font-medium">
              No attempts remaining
            </div>
          )}
          {item.statusInfo.completedAt && (
            <div>
              Last completed:{" "}
              {new Date(item.statusInfo.completedAt).toLocaleDateString()}
            </div>
          )}
          {/* Show best score if available */}
          {item.statusInfo?.attemptScores &&
            item.statusInfo.attemptScores.length > 0 && (
              <div className="text-green-600 font-medium">
                Best:{" "}
                {Math.max(...item.statusInfo.attemptScores.map((a) => a.score))}
                /{item.questions?.length || 0}
              </div>
            )}
        </div>
      );
    }

    return (
      <div className="text-xs text-gray-600 text-center">
        <div>{maxAttempts} attempts available</div>
      </div>
    );
  };

  return (
    <div>
      <Breadcrumb actionButtons={[]} />
      <Tabs tabs={visibleTabs} />
      {!isVisibleMechanical ? (
        <NotFound />
      ) : (
        <>
          {isLoading || isValidating ? (
            <Loader />
          ) : data?.data?.length === 0 ? (
            <span className=" mt-2  flex flex-col justify-center gap-2 text-center items-center">
              No available content. Purchase simulator to get started.
              <Link to={publicUrls.simulatorMechanical}>
                <Button isLoading={isLoading}>Simulator</Button>
              </Link>
            </span>
          ) : data?.data?.purchased ? (
            (() => {
              // const purchaseInfo = data?.status;
              const purchasedAt = data?.data?.purchasedAt
                ? new Date(data?.data?.purchasedAt)
                : null;
              const duration = data?.data?.subscriptionDuration
                ? parseInt(data?.data?.subscriptionDuration, 10)
                : 0;

              if (!purchasedAt || isNaN(duration) || duration <= 0) {
                return (
                  <div className="text-red-600 font-semibold">
                    ⚠️ Subscription data is missing or invalid.
                  </div>
                );
              }

              const expirationDate = new Date(purchasedAt);
              expirationDate.setDate(expirationDate.getDate() + duration);

              const today = new Date();
              const remainingDays = Math.max(
                0,
                Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24))
              );

              return (
                <>
                  <p className="font-semibold mt-4 mb-4">
                    <span className="text-gray-700">
                      {remainingDays > 0
                        ? ` Your subscription expires in ${remainingDays} day${
                            remainingDays > 1 ? "s" : ""
                          }.`
                        : "Your subscription has expired."}
                    </span>
                  </p>
                  <div className="container mx-auto grid grid-cols-4 p-6 gap-2">
                    {data.data?.modelTest?.map((item) => {
                      const canInteract = canInteractWithExam(item);
                      const hasCompletedAttempts =
                        item.statusInfo &&
                        item.statusInfo.attempts > 0 &&
                        (item.statusInfo.status === "completed" ||
                          item.statusInfo.completedAt);

                      return (
                        <div
                          key={item?._id}
                          className="bg-white shadow-lg hover:shadow-xl rounded-lg p-4 transition-all duration-300"
                        >
                          <button className="block w-full flex justify-center">
                            <img
                              src="/examImage.png"
                              alt={item?.title}
                              className="w-36 h-36 object-cover transform group-hover:scale-110 transition-transform duration-300"
                            />
                          </button>
                          <div className="flex flex-col items-center mt-3 gap-1">
                            <h2 className="text-sm font-semibold">
                              {item?.title}
                            </h2>
                            <p className="text-xs">{item?.description}</p>
                            <p className="text-xs">
                              Total Time: {item?.duration} hour
                            </p>

                            {/* Show attempt status */}
                            {getAttemptStatusDisplay(item)}

                            <div className="flex gap-2 mt-2 w-full">
                              {/* Start/Continue/Resume Exam Button */}
                              <Button
                                size="small"
                                onClick={() => onStartClick(item)}
                                disabled={!canInteract}
                                className="flex-1"
                              >
                                {getAttemptButtonText(item)}
                              </Button>

                              {/* See Attempts Button - Show if user has completed attempts */}
                              {hasCompletedAttempts && (
                                <Button
                                  size="small"
                                  variant="outline"
                                  onClick={() => onSeeAttemptsClick(item)}
                                  className="flex-1"
                                >
                                  See Attempts
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()
          ) : (
            <span className=" mt-2  flex flex-col justify-center gap-2 text-center items-center">
              No available content. Purchase simulator to get started.
              <Link to={publicUrls.simulatorMechanical}>
                <Button isLoading={isLoading}>Simulator</Button>
              </Link>
            </span>
          )}

          <SimulatorModal
            selected={selected}
            isOpen={showModal}
            onClose={handleModalClose}
            mode={modalMode}
            selectedAttemptNumber={selectedAttemptNumber}
            onAttemptNumberChange={setSelectedAttemptNumber}
          />
        </>
      )}
    </div>
  );
};

export default MechanicalStudentSimulator;
